import React from "react";
import {
  Stethoscope,
  ClipboardList,
  ShieldCheck,
  CalendarClock,
  Wrench,
  Receipt,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import taskTable from "../../assets/HC/hctable.png";
import calendarView from "../../assets/HC/hccalendar.png";
import peekView from "../../assets/HC/hcpeekview.png";
import reminders from "../../assets/HC/hcreminders.png";
import documents from "../../assets/HC/hcdocuments.png";

const features = [
  {
    icon: <ClipboardList size={26} />,
    title: "Patient Intake & Coordination",
    description:
      "Registration paperwork, insurance verification, and referral coordination tracked as tasks, not sticky notes at the front desk.",
  },
  {
    icon: <ShieldCheck size={26} />,
    title: "Compliance & Accreditation",
    description:
      "NABH/NABL renewals, biomedical waste audits, and license expiries tracked with due dates, so nothing surfaces only during an inspection.",
  },
  {
    icon: <CalendarClock size={26} />,
    title: "Staff Scheduling",
    description:
      "Duty rosters, on-call schedules, and leave coverage assigned and tracked, so shift gaps show up before the ward does.",
  },
  {
    icon: <Wrench size={26} />,
    title: "Vendor & Equipment Management",
    description:
      "Equipment AMC renewals, pharmacy stock follow-ups, and vendor coordination, tracked with clear owners and dates.",
  },
];

const showcase = [
  {
    eyebrow: "Every Department, One Task Board",
    title: "Hospital Administration to Radiology, tracked in one place",
    description:
      "Hospital Administration, Biomedical Equipment, Operation Theatre, Medical Records, Radiology, Nursing, Patient Registration, and OPD Management — every department's tasks tracked with hospital, status, period, and financial year in one board.",
    image: taskTable,
    alt: "Simplifyte task table showing hospital tasks across Hospital Administration, Biomedical Equipment, Operation Theatre, Radiology, and Nursing Department with hospital and status columns",
  },
  {
    eyebrow: "Never Miss a Deadline",
    title: "Today's due tasks, overdue items, and the month ahead",
    description:
      "An overdue count you can't miss, today's due tasks listed right alongside the calendar — staffing a night shift, scheduling an MRI, reporting faulty equipment — and due/closed counts for every day of the month.",
    image: calendarView,
    alt: "Simplifyte Calendar View showing an overdue banner, today's due hospital tasks, and a monthly calendar with due and closed counts",
  },
  {
    eyebrow: "Every Task, With Full Context",
    title: "Open any task without losing the bigger picture",
    description:
      "A task like 'Confirm OT schedule for tomorrow' opens in a side peek panel with its hospital, financial year, period, due date, and assignee — full context, without leaving the task list behind.",
    image: peekView,
    alt: "Simplifyte task peek panel showing Confirm OT schedule for tomorrow under Operation Theatre - Lifeline Hospital, with financial year, due date, and assignee",
  },
  {
    eyebrow: "Reminders",
    title: "Daily care routines and compliance checks, nudged on time",
    description:
      "Overdue and due-today reminders — checking expiring medicines, verifying daily stock, reviewing critical patient alerts, following up on lab results — so ward and pharmacy routines don't quietly slip.",
    image: reminders,
    alt: "Simplifyte Reminders screen showing overdue and pending hospital reminders like check expiring medicines, verify daily medicine stock, and review critical patient alerts",
  },
  {
    eyebrow: "Patient Documents",
    title: "ICU records, lab reports, and insurance files — organized by patient",
    description:
      "Patient Records, Lab Reports, Radiology Reports, Medical Reports, Insurance Documents, and ICU Records — every folder in one place, shared with the right staff, without digging through email attachments.",
    image: documents,
    alt: "Simplifyte User Documents screen showing healthcare folders for Patient Records, Lab Reports, Radiology Reports, Medical Reports, Insurance Documents, and ICU Records",
  },
];

const benefits = [
  "Cut time spent chasing patient paperwork and referral follow-ups",
  "Track NABH, biomedical waste, and license renewals before they lapse",
  "Give every department clear ownership of duty rosters and shifts",
  "Get overdue and due-today nudges for pharmacy stock, patient alerts, and lab follow-ups",
  "Keep patient records, lab reports, and insurance documents organized in one place",
];

const HealthcareProvidersPage = () => {
  return (
    <>
      <HeaderMenu />
      <div className="Healthcare_class_main">
        {/* Hero */}
        <section className="Healthcare_class_hero">
          <div className="Healthcare_class_container">
            <div className="Healthcare_class_hero_content">
              <div className="Healthcare_class_icon_box">
                <Stethoscope size={34} />
              </div>

              <h1 className="Healthcare_class_heading">
                Workflow software built for Healthcare providers
              </h1>

              <p className="Healthcare_class_subheading">
                Coordinate patient intake, staff schedules, compliance, and
                vendor follow-ups in one place your whole team can trust.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Healthcare_class_features_section">
          <div className="Healthcare_class_container">
            <h2 className="Healthcare_class_section_title Healthcare_class_center">
              Everything a clinic or hospital's back office needs
            </h2>

            <div className="Healthcare_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Healthcare_class_feature_card">
                  <div className="Healthcare_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Healthcare_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Healthcare_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Healthcare_class_showcase_section">
          <div className="Healthcare_class_container">
            <h2 className="Healthcare_class_section_title Healthcare_class_center">
              Built for the pace of patient care
            </h2>
            <p className="Healthcare_class_showcase_subtitle">
              Real screens from the product — task boards, calendars, reminders,
              and patient documents your whole team can rely on.
            </p>

            <div className="Healthcare_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Healthcare_class_showcase_row ${
                    index % 2 === 1
                      ? "Healthcare_class_showcase_row_reverse"
                      : ""
                  }`}
                >
                  <div className="Healthcare_class_showcase_text">
                    <span className="Healthcare_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Healthcare_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Healthcare_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Healthcare_class_showcase_media">
                    <div className="Healthcare_class_showcase_frame_outer">
                      <div className="Healthcare_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Healthcare_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Healthcare_class_benefits_section">
          <div className="Healthcare_class_container">
            <div className="Healthcare_class_benefits_wrapper">
              <h2 className="Healthcare_class_section_title Healthcare_class_center">
                Why healthcare providers choose Simplifyte
              </h2>

              <div className="Healthcare_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Healthcare_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="Healthcare_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Healthcare_class_cta_section">
          <div className="Healthcare_class_container">
            <div className="Healthcare_class_cta_box">
              <h2 className="Healthcare_class_cta_title">
                Ready to bring clarity to patient care operations?
              </h2>

              <p className="Healthcare_class_cta_text">
                Join healthcare teams who've streamlined scheduling,
                compliance, and vendor follow-ups.
              </p>

              <button
                className="Healthcare_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default HealthcareProvidersPage;
